import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { useBuyer } from "../../contexts/BuyerContext";
import Header from "../../components/Header";
import { CheckCircle, XCircle, Eye } from "lucide-react";
import "./OpenMarketStatus.css";

const statusConfig = {
  qcchecking: {
    status: "requested",
    title: "QC Checking",
    description: "Open Market products pending QC approval",
    color: "blue",
    icon: <Eye size={20} />,
    actions: ["approve", "reject"],
  },
  approved: {
    status: "approved",
    title: "Approved Products",
    description: "Open Market products approved by QC",
    color: "green",
    icon: <CheckCircle size={20} />,
    actions: [],
  },
  rejected: {
    status: "rejected",
    title: "Rejected Products",
    description: "Open Market products rejected by QC",
    color: "red",
    icon: <XCircle size={20} />,
    actions: [],
  },
};

const OpenMarketStatusPage = () => {
  const { type } = useParams();
  const { buyer } = useBuyer();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = useMemo(() => statusConfig[type] || statusConfig.qcchecking, [type]);

  const fetchProducts = useCallback(async () => {
    const buyerId = buyer?.buyerId;
    if (!buyerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "products"),
        where("buyerId", "==", buyerId),
        where("serviceType", "==", "openmarket")
      );

      const snapshot = await getDocs(q);
      const allProducts = snapshot.docs.map((docSnap) => ({
        firebaseDocId: docSnap.id,
        ...docSnap.data(),
      }));

      const filtered = allProducts.filter((product) => {
        const status = String(product.status || "").toLowerCase();
        const requestStatus = String(product.requestStatus || "").toLowerCase();
        if (config.status === "requested") {
          return status === "requested" || requestStatus === "pending";
        }
        return status === config.status;
      });

      setProducts(filtered);
      console.log(`[OpenMarket${config.title}] Fetched ${filtered.length} products from ${allProducts.length} total`);
    } catch (err) {
      console.error(`Error fetching ${config.title}:`, err);
      setError(`Failed to load ${config.title}.`);
    } finally {
      setLoading(false);
    }
  }, [buyer?.buyerId, config.status, config.title]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);



  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`, { state: { product } });
  };

  const getLotSummary = (product) => {
    if (!product.lotDetails || product.lotDetails.length === 0) return null;
    return product.lotDetails
      .map((lot) => `${lot.size}: ${lot.avgNetWt}g x ${lot.set}`)
      .join(", ");
  };

  if (loading) {
    return (
      <div className="openmarket-status-container">
        <Header title={config.title} />
        <div className="loading-state">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="openmarket-status-container">
        <Header title={config.title} />
        <div className="error-state">
          <p className="error">{error}</p>
          <button onClick={fetchProducts} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <><Header title={config.title} />
      <div className="openmarket-status-container">

        {products.length === 0 ? (
          <div className="empty-state">
            <p>No {config.title.toLowerCase()} found.</p>
            <button onClick={fetchProducts} className="retry-btn">
              Refresh
            </button>
          </div>
        ) : (
          <div className="openmarket-grid">
            {products.map((product) => {
              const imageUrl = product.images?.[0]?.url?.trim();
              const lotSummary = getLotSummary(product);

              return (
                <div key={product.firebaseDocId} className="openmarket-card">
                  <div className="openmarket-image" onClick={() => handleProductClick(product)}>
                    {imageUrl ? (
                      <img src={imageUrl} alt={product.productName || "Product"} loading="lazy" />
                    ) : (
                      <div className="image-placeholder">No Image</div>
                    )}
                    <div className={`status-badge-overlay status-${config.color}`}>
                      {config.icon}
                      <span>{config.title}</span>
                    </div>
                  </div>

                  <div className="openmarket-info">
                    <span className="product-spec">{product.category}/{product.productName}</span>
                    <p className="product-name">{product.specification}/{product.styleType}</p>

                    {lotSummary && (
                      <div className="lot-summary">
                        <strong>Lot:</strong> {lotSummary}
                      </div>
                    )}

                    <div className="product-meta">
                      <span>Segment: {product.segment}</span>
                      <span>Source: {product.productSource}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default OpenMarketStatusPage;
